import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import Pagination from '../components/Pagination';
import apiService from '../services/api';
import oliveLogo from '../assets/images/olive-logo.png';
import '../styles/StaffPages.css';

const PAGE_SIZE = 500;
const KM_PER_MILE = 1.609344;
const kmToMiles = (km) => (typeof km === 'number' ? km / KM_PER_MILE : null);

const SuperuserOliveMerchants = () => {
  const [merchants, setMerchants] = useState([]);
  const [cached, setCached] = useState(false);
  const [pagesFetched, setPagesFetched] = useState(0);
  const [fetchErrors, setFetchErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  // Nearby-offers map state
  const [mapOpen, setMapOpen] = useState(false);
  const [mapLat, setMapLat] = useState('');
  const [mapLng, setMapLng] = useState('');
  const [mapRadiusMi, setMapRadiusMi] = useState('60');
  const [nearbyResults, setNearbyResults] = useState(null); // null = brand mode; array = nearby mode
  const [nearbyCenter, setNearbyCenter] = useState(null);   // { lat, lng } of the searched point
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [nearbyError, setNearbyError] = useState(null);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const mapElRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerLayerRef = useRef(null);
  const markerByOfferIdRef = useRef({});
  const iconRegistryRef = useRef({ idle: null, active: null });
  const navigate = useNavigate();

  const fetchData = async ({ forceRefresh = false } = {}) => {
    try {
      if (forceRefresh) setRefreshing(true);
      const res = await apiService.getAdminOliveMerchants({ forceRefresh });
      setMerchants(Array.isArray(res?.results) ? res.results : []);
      setCached(Boolean(res?.cached));
      setPagesFetched(Number(res?.pages_fetched || 0));
      setFetchErrors(Array.isArray(res?.errors) ? res.errors : []);
      setError(null);
    } catch (err) {
      console.error('Failed to load olive merchants:', err);
      setError('Failed to load Olive merchants. The Olive API may be slow or unavailable.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        await fetchData();
      } catch (err) {
        console.error('Failed auth check:', err);
        navigate('/login/');
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // When `nearbyResults` is non-null, the table shows those offers (each row
  // is an OFFER, not a deduped brand). We normalize the shape so the existing
  // table renderer can stay simple, and sort by distance ascending so the
  // closest offer is always first.
  const isNearby = Array.isArray(nearbyResults);
  const tableSource = useMemo(() => {
    if (!isNearby) return merchants;
    const mapped = nearbyResults.map((r, idx) => ({
      brand_id: `${r.brand_id || ''}-${idx}`,  // need unique React key per row
      name: r.brand_name || '',
      logo_url: r.logo_url || '',
      website: r.website || '',
      category: r.category || '',
      sample_offer_id: r.offer_id || '',
      max_rate: r.max_rate || '',
      store: r.store || null,
    }));
    mapped.sort((a, b) => {
      const da = a.store && typeof a.store.distance_km === 'number' ? a.store.distance_km : Infinity;
      const db = b.store && typeof b.store.distance_km === 'number' ? b.store.distance_km : Infinity;
      return da - db;
    });
    return mapped;
  }, [merchants, nearbyResults, isNearby]);

  const gridCols = isNearby
    ? '64px 1fr 110px 120px 1fr'
    : '64px 1fr 110px 1fr';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tableSource;
    return tableSource.filter((m) => {
      const inName = (m.name || '').toLowerCase().includes(q);
      const inId = String(m.brand_id || '').toLowerCase().includes(q);
      const inOfferId = String(m.sample_offer_id || '').toLowerCase().includes(q);
      const inCategory = (m.category || '').toLowerCase().includes(q);
      return inName || inId || inOfferId || inCategory;
    });
  }, [tableSource, query]);

  useEffect(() => { setPage(1); }, [query]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleCopy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedId(key);
      setTimeout(() => setCopiedId((cur) => (cur === key ? null : cur)), 1200);
    } catch (e) {
      console.error('Clipboard write failed:', e);
    }
  };

  const handleNearbySearch = async () => {
    const lat = parseFloat(mapLat);
    const lng = parseFloat(mapLng);
    const radiusMi = Math.max(1, Math.min(310, parseInt(mapRadiusMi, 10) || 60));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setNearbyError('Enter valid latitude and longitude.');
      return;
    }
    setNearbyError(null);
    setNearbyLoading(true);
    try {
      const res = await apiService.getAdminOliveNearby({
        lat,
        lng,
        limit: 100,
        radiusKm: Math.ceil(radiusMi * KM_PER_MILE),
      });
      setNearbyResults(Array.isArray(res?.results) ? res.results : []);
      setNearbyCenter({ lat, lng });
      setPage(1);
      setSelectedOfferId(null);
    } catch (err) {
      console.error('Nearby search failed:', err);
      setNearbyError(err?.response?.data?.error || 'Failed to load nearby offers.');
    } finally {
      setNearbyLoading(false);
    }
  };

  const clearNearby = () => {
    setNearbyResults(null);
    setNearbyCenter(null);
    setNearbyError(null);
    setSelectedOfferId(null);
    setPage(1);
    if (markerLayerRef.current) {
      try { markerLayerRef.current.clearLayers(); } catch (_) {}
    }
  };

  // Initialize the Leaflet map when the panel is open and the script is loaded.
  useEffect(() => {
    if (!mapOpen) return undefined;
    let timer;
    const init = () => {
      if (!window.L || !mapElRef.current) {
        timer = setTimeout(init, 100);
        return;
      }
      if (mapInstanceRef.current) {
        // Already initialized — just invalidate size in case container resized while collapsed.
        mapInstanceRef.current.invalidateSize();
        return;
      }
      const center = [parseFloat(mapLat) || 39.5, parseFloat(mapLng) || -98.35];
      const map = window.L.map(mapElRef.current).setView(center, 4);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
      }).addTo(map);
      markerLayerRef.current = window.L.layerGroup().addTo(map);
      mapInstanceRef.current = map;
    };
    init();
    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapOpen]);

  // Refresh markers + center whenever nearby results change (NOT on selection
  // change — a separate effect just swaps the active marker's icon below).
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;
    if (!markerLayerRef.current) {
      markerLayerRef.current = window.L.layerGroup().addTo(mapInstanceRef.current);
    } else {
      markerLayerRef.current.clearLayers();
    }
    markerByOfferIdRef.current = {};

    const points = [];

    // Helper to build a colored pin divIcon. SVG matches Leaflet's
    // standard teardrop pin shape; pass any hex for the fill.
    const buildPinIcon = (fill, stroke, className) => {
      // Render the SVG at the natural origin (0,0) of the icon container.
      // Leaflet's `iconAnchor` places the (12, 34) point of the container at
      // the marker's lat/lng — i.e. the SVG's tip — and `popupAnchor` opens
      // the popup directly above that tip. No CSS transforms here, because
      // Leaflet doesn't know about transforms and the popup would land off.
      const html =
        `<svg viewBox="0 0 24 34" width="24" height="34" xmlns="http://www.w3.org/2000/svg" style="display:block">` +
          `<path d="M12 0c6.6 0 12 5.4 12 12 0 9-12 22-12 22S0 21 0 12C0 5.4 5.4 0 12 0z" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>` +
          `<circle cx="12" cy="12" r="4" fill="#fff"/>` +
        `</svg>`;
      return window.L.divIcon({
        className,
        html,
        iconSize: [24, 34],
        iconAnchor: [12, 34],
        popupAnchor: [0, -32],
      });
    };

    // Mid-tone shades — clearer than pure pastels, softer than the original
    // saturated brand colors. Center = sky blue, offers = lavender,
    // selected = deeper purple.
    const centerIcon = buildPinIcon('#60a5fa', '#3b82f6', 'hc-olive-center-pin');
    const offerIcon = buildPinIcon('#a78bfa', '#8b5cf6', 'hc-olive-offer-pin');
    const offerIconActive = buildPinIcon('#7c3aed', '#6d28d9', 'hc-olive-offer-pin hc-active');
    iconRegistryRef.current = { idle: offerIcon, active: offerIconActive };

    // Blue pin at the searched coordinates so the user's location stands out.
    if (nearbyCenter && Number.isFinite(nearbyCenter.lat) && Number.isFinite(nearbyCenter.lng)) {
      window.L.marker([nearbyCenter.lat, nearbyCenter.lng], {
        icon: centerIcon,
        zIndexOffset: 1000,  // float above the purple offer pins
      })
        .addTo(markerLayerRef.current)
        .bindPopup(
          `<div style="font-family:-apple-system,sans-serif;font-size:13px;line-height:1.4">
             <strong>Your location</strong><br/>
             ${nearbyCenter.lat.toFixed(5)}, ${nearbyCenter.lng.toFixed(5)}
           </div>`
        );
      points.push([nearbyCenter.lat, nearbyCenter.lng]);
    }

    if (Array.isArray(nearbyResults)) {
      nearbyResults.forEach((r) => {
        const s = r.store;
        if (!s || typeof s.lat !== 'number' || typeof s.lng !== 'number') return;
        points.push([s.lat, s.lng]);
        const isActive = selectedOfferId && r.offer_id === selectedOfferId;
        const miles = kmToMiles(s.distance_km);
        const popup =
          `<div style="font-family:-apple-system,sans-serif;font-size:13px;line-height:1.4">` +
          `<strong>${(r.brand_name || '').replace(/</g, '&lt;')}</strong>` +
          (r.max_rate ? `<br/><span style="color:#7c3aed">${r.max_rate}</span>` : '') +
          (s.address ? `<br/>${s.address}, ${s.city || ''} ${s.state || ''}` : '') +
          (typeof miles === 'number' ? `<br/><span style="color:#6b7280">${miles.toFixed(1)} mi away</span>` : '') +
          `</div>`;
        const marker = window.L.marker([s.lat, s.lng], {
          icon: isActive ? offerIconActive : offerIcon,
          zIndexOffset: isActive ? 800 : 0,
        })
          .addTo(markerLayerRef.current)
          .bindPopup(popup);
        if (r.offer_id) {
          markerByOfferIdRef.current[r.offer_id] = marker;
          marker.on('click', () => setSelectedOfferId(r.offer_id));
        }
      });
    }

    // After a search, center on the user's location at a city-level zoom so
    // their pin is the focus. If we somehow only have offer markers (no
    // explicit center), fall back to fitBounds across them.
    if (nearbyCenter && Number.isFinite(nearbyCenter.lat) && Number.isFinite(nearbyCenter.lng)) {
      mapInstanceRef.current.setView([nearbyCenter.lat, nearbyCenter.lng], 13);
    } else if (points.length > 0) {
      try {
        mapInstanceRef.current.fitBounds(points, { padding: [24, 24], maxZoom: 13 });
      } catch (_) {
        mapInstanceRef.current.setView(points[0], 12);
      }
    }
  }, [nearbyResults, nearbyCenter]);

  // Swap just the active marker's icon when selection changes; don't refit.
  useEffect(() => {
    const idle = iconRegistryRef.current.idle;
    const active = iconRegistryRef.current.active;
    if (!idle || !active) return;
    Object.entries(markerByOfferIdRef.current).forEach(([offerId, marker]) => {
      if (offerId === selectedOfferId) {
        marker.setIcon(active);
        marker.setZIndexOffset(800);
        if (mapInstanceRef.current) {
          try { marker.openPopup(); } catch (_) {}
        }
      } else {
        marker.setIcon(idle);
        marker.setZIndexOffset(0);
      }
    });
  }, [selectedOfferId]);

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton to="/superuser/dashboard/" text="Back to Dashboard" />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <h1 className="staff-page-header" style={{ margin: 0 }}>Olive Merchants</h1>
            <img
              src={oliveLogo}
              alt="Olive"
              style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
            />
          </div>
          <p className="staff-page-subheader">
            All merchants in the Olive system.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by brand name, brand ID, offer ID, or category..."
              style={{
                flex: 1,
                minWidth: '280px',
                maxWidth: '520px',
                padding: '12px 16px',
                border: '1px solid #dee2e6',
                borderRadius: '100px',
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={() => fetchData({ forceRefresh: true })}
              disabled={refreshing || loading}
              style={{
                padding: '12px 20px',
                border: '1px solid #dee2e6',
                borderRadius: '100px',
                backgroundColor: 'white',
                fontFamily: '"Baikal Book"',
                fontSize: '13px',
                color: '#666',
                cursor: refreshing || loading ? 'not-allowed' : 'pointer',
                opacity: refreshing || loading ? 0.6 : 1,
              }}
            >
              {refreshing ? 'Refreshing…' : 'Force refresh'}
            </button>
            <div style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#666' }}>
              {loading
                ? 'Loading…'
                : (
                  <>
                    {filtered.length.toLocaleString()} of {merchants.length.toLocaleString()} offers
                  </>
                )
              }
            </div>
          </div>

          {/* Collapsible Map / Nearby Offers panel */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #80dfff',
            marginBottom: '16px',
            overflow: 'hidden',
          }}>
            <button
              type="button"
              onClick={() => setMapOpen((v) => !v)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '18px 22px',
                backgroundColor: mapOpen ? '#00C8FF' : '#e0f7ff',
                border: 'none',
                cursor: 'pointer',
                fontFamily: '"Baikal Book"',
                fontSize: '17px',
                fontWeight: 600,
                color: '#000000',
                textAlign: 'left',
                transition: 'background-color 0.15s ease',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
                </svg>
                Map
                {nearbyResults && nearbyResults.length > 0 && (
                  <span style={{
                    padding: '2px 10px',
                    backgroundColor: '#e0f7ff',
                    color: '#0369a1',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}>
                    {nearbyResults.length.toLocaleString()} nearby offers
                  </span>
                )}
              </span>
              <span style={{ fontSize: '18px', color: '#000', fontWeight: 700 }}>
                {mapOpen ? '▾' : '▸'}
              </span>
            </button>

            {/* Panel body stays mounted even when collapsed so Leaflet keeps
                its DOM container alive — toggling display:none and re-opening
                would otherwise leave Leaflet pointing at a stale element. */}
            <div style={{
              display: mapOpen ? 'block' : 'none',
              padding: '0 18px 18px',
              borderTop: '1px solid #f3f4f6',
            }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontFamily: '"Baikal Book"', color: '#374151', marginBottom: '4px' }}>
                      Latitude
                    </label>
                    <input
                      type="text"
                      value={mapLat}
                      onChange={(e) => setMapLat(e.target.value)}
                      placeholder="e.g. 35.7796"
                      style={{
                        width: '180px',
                        padding: '10px 14px',
                        border: '1px solid #dee2e6',
                        borderRadius: '100px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontFamily: '"Baikal Book"', color: '#374151', marginBottom: '4px' }}>
                      Longitude
                    </label>
                    <input
                      type="text"
                      value={mapLng}
                      onChange={(e) => setMapLng(e.target.value)}
                      placeholder="e.g. -78.6382"
                      style={{
                        width: '180px',
                        padding: '10px 14px',
                        border: '1px solid #dee2e6',
                        borderRadius: '100px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontFamily: '"Baikal Book"', color: '#374151', marginBottom: '4px' }}>
                      Radius (mi)
                    </label>
                    <input
                      type="text"
                      value={mapRadiusMi}
                      onChange={(e) => setMapRadiusMi(e.target.value)}
                      placeholder="60"
                      style={{
                        width: '100px',
                        padding: '10px 14px',
                        border: '1px solid #dee2e6',
                        borderRadius: '100px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleNearbySearch}
                    disabled={nearbyLoading}
                    style={{
                      padding: '10px 22px',
                      backgroundColor: '#00C8FF',
                      border: '1px solid #00C8FF',
                      borderRadius: '100px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      color: '#000',
                      fontWeight: 500,
                      cursor: nearbyLoading ? 'not-allowed' : 'pointer',
                      opacity: nearbyLoading ? 0.6 : 1,
                    }}
                  >
                    {nearbyLoading ? 'Searching…' : 'Find nearby offers'}
                  </button>
                  {nearbyResults && (
                    <button
                      type="button"
                      onClick={clearNearby}
                      style={{
                        padding: '10px 18px',
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '100px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '13px',
                        color: '#666',
                        cursor: 'pointer',
                      }}
                    >
                      Clear nearby
                    </button>
                  )}
                </div>

                {nearbyError && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px 14px',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    color: '#991b1b',
                    fontFamily: '"Baikal Book"',
                    fontSize: '13px',
                  }}>
                    {nearbyError}
                  </div>
                )}

                <div
                  ref={mapElRef}
                  style={{
                    width: '100%',
                    height: '380px',
                    marginTop: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#f3f4f6',
                  }}
                />
            </div>
          </div>

          {fetchErrors.length > 0 && (
            <div style={{
              padding: '12px 14px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '8px',
              color: '#92400e',
              fontFamily: '"Baikal Book"',
              fontSize: '13px',
              marginBottom: '16px',
            }}>
              Olive API returned partial results. {fetchErrors.length} fetch error{fetchErrors.length === 1 ? '' : 's'} encountered.
            </div>
          )}

          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontFamily: '"Baikal Book"',
              fontSize: '14px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}

          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}>
            {!loading && (
              <Pagination
                position="top"
                page={page}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              gap: '12px',
              padding: '14px 18px',
              backgroundColor: '#f9fafb',
              fontFamily: '"Baikal Book"',
              fontSize: '12px',
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              borderBottom: '1px solid #e5e7eb',
            }}>
              <div>Logo</div>
              <div>Brand</div>
              <div>Max Rate</div>
              {isNearby && <div>Distance</div>}
              <div>Offer ID</div>
            </div>

            {loading && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                Loading Olive merchants… this can take a few seconds.
              </div>
            )}

            {!loading && paged.map((m) => {
              const isRowSelected = isNearby && selectedOfferId && m.sample_offer_id === selectedOfferId;
              return (
              <div
                key={m.brand_id}
                onClick={() => isNearby && m.sample_offer_id && setSelectedOfferId(m.sample_offer_id === selectedOfferId ? null : m.sample_offer_id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  gap: '12px',
                  alignItems: 'center',
                  padding: '12px 18px',
                  borderBottom: '1px solid #f3f4f6',
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  cursor: isNearby ? 'pointer' : 'default',
                  backgroundColor: isRowSelected ? '#ede9fe' : 'transparent',
                  borderLeft: isRowSelected ? '4px solid #7c3aed' : '4px solid transparent',
                  transition: 'background-color 0.15s ease, border-color 0.15s ease',
                }}
              >
                <div>
                  {m.logo_url ? (
                    <img
                      src={m.logo_url}
                      alt={m.name}
                      style={{ width: '44px', height: '44px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: 'white' }}
                    />
                  ) : (
                    <div style={{ width: '44px', height: '44px', borderRadius: '6px', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '11px' }}>
                      No logo
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: '#111827' }}>{m.name || '(unnamed)'}</div>
                  {m.category && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{m.category}</div>
                  )}
                  {m.store && (m.store.address || m.store.city) && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      {[m.store.address, m.store.city, m.store.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {m.website && (
                    <a href={m.website} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: '2px', fontSize: '12px', color: '#0369a1', textDecoration: 'none' }}>
                      {m.website.replace(/^https?:\/\//, '').replace(/\/.*$/, '')}
                    </a>
                  )}
                </div>
                <div style={{ fontSize: '13px', color: m.max_rate ? '#111827' : '#9ca3af', fontWeight: m.max_rate ? 500 : 400 }}>
                  {m.max_rate || '—'}
                </div>
                {isNearby && (() => {
                  const miles = m.store && typeof m.store.distance_km === 'number' ? kmToMiles(m.store.distance_km) : null;
                  return (
                    <div style={{ fontSize: '13px', color: miles !== null ? '#0369a1' : '#9ca3af', fontWeight: 500 }}>
                      {miles !== null ? `${miles.toFixed(1)} mi` : '—'}
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {m.sample_offer_id || '—'}
                  </span>
                  {m.sample_offer_id && (
                    <button
                      type="button"
                      onClick={() => handleCopy(`offer-${m.brand_id}`, m.sample_offer_id)}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: copiedId === `offer-${m.brand_id}` ? '#00C8FF' : 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '100px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '11px',
                        color: copiedId === `offer-${m.brand_id}` ? '#000' : '#666',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {copiedId === `offer-${m.brand_id}` ? '✓' : 'Copy ID'}
                    </button>
                  )}
                </div>
              </div>
              );
            })}

            {!loading && filtered.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280', fontFamily: '"Baikal Book"', fontSize: '14px' }}>
                {query ? `No brands match "${query}".` : 'No Olive merchants returned.'}
              </div>
            )}

            {!loading && (
              <Pagination
                page={page}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperuserOliveMerchants;
